const CLIENT_ID = '111155376912-96c48rso744m0qi4gq3kr05992986h2m.apps.googleusercontent.com';
const SCOPES = 'https://www.googleapis.com/auth/drive.file';
const FOLDER_NAME = 'StudyBoard Backups';
const DRIVE_CONNECTED_KEY = 'sb_drive_connected';

let tokenClient: any = null;
let accessToken: string | null = null;
let onSignInCallback: ((connected: boolean) => void) | null = null;

export interface DriveBackupFile {
    id: string;
    name: string;
    modifiedTime: string;
    size: string;
}

export const initGoogleDrive = (onSignIn: (connected: boolean) => void) => {
    onSignInCallback = onSignIn;

    const trySetup = (): boolean => {
        const google = (window as any).google;
        if (!google?.accounts?.oauth2) return false;

        tokenClient = google.accounts.oauth2.initTokenClient({
            client_id: CLIENT_ID,
            scope: SCOPES,
            callback: (resp: any) => {
                if (resp.error) { onSignIn(false); return; }
                accessToken = resp.access_token;
                localStorage.setItem(DRIVE_CONNECTED_KEY, '1');
                onSignIn(true);
            },
            error_callback: () => { onSignIn(false); },
        });

        if (localStorage.getItem(DRIVE_CONNECTED_KEY)) {
            tokenClient.requestAccessToken({ prompt: '' });
        }
        return true;
    };

    if (trySetup()) return;
    // Same GIS async/defer race as youtubeService.initYouTube — poll briefly instead of leaving
    // tokenClient null forever if this runs before the script tag finishes loading.
    let attempts = 0;
    const interval = setInterval(() => {
        attempts++;
        if (trySetup() || attempts >= 50) clearInterval(interval);
    }, 200);
};

export const connectDrive = () => {
    if (!tokenClient) return;
    const prompt = localStorage.getItem(DRIVE_CONNECTED_KEY) ? '' : 'consent';
    tokenClient.requestAccessToken({ prompt });
};

export const disconnectDrive = () => {
    const google = (window as any).google;
    if (accessToken) {
        google?.accounts?.oauth2?.revoke(accessToken, () => {});
        accessToken = null;
    }
    localStorage.removeItem(DRIVE_CONNECTED_KEY);
    if (onSignInCallback) onSignInCallback(false);
};

export const isDriveConnected = () => !!accessToken;

const getOrCreateFolder = async (): Promise<string> => {
    const searchRes = await fetch(
        `https://www.googleapis.com/drive/v3/files?${new URLSearchParams({
            q: `name='${FOLDER_NAME}' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
            fields: 'files(id)',
        })}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    const searchData = await searchRes.json();
    if (searchData.files?.length > 0) return searchData.files[0].id;

    const createRes = await fetch('https://www.googleapis.com/drive/v3/files', {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: FOLDER_NAME, mimeType: 'application/vnd.google-apps.folder' }),
    });
    const folder = await createRes.json();
    return folder.id;
};

export const downloadDriveBackup = async (fileId: string): Promise<Blob> => {
    if (!accessToken) throw new Error('Not connected to Google Drive');
    const res = await fetch(
        `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (!res.ok) throw new Error('Failed to download backup');
    return res.blob();
};

// Always creates a new file on Drive; returns the file record for caller to persist.
export const saveZipToDrive = async (blob: Blob, filename: string): Promise<DriveBackupFile> => {
    if (!accessToken) throw new Error('Not connected to Google Drive');

    const folderId = await getOrCreateFolder().catch(() => null);
    const metadata = folderId ? { name: filename, parents: [folderId] } : { name: filename };

    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', blob, filename);

    const res = await fetch(
        'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,modifiedTime,size',
        { method: 'POST', headers: { Authorization: `Bearer ${accessToken}` }, body: form }
    );
    const data = await res.json();
    if (!res.ok) throw new Error(data.error?.message ?? 'Upload failed');

    return {
        id: data.id,
        name: data.name ?? filename,
        modifiedTime: data.modifiedTime ?? new Date().toISOString(),
        size: data.size ?? String(blob.size),
    };
};
